import { Box, Flex, Text } from "@chakra-ui/react";
import { forwardRef, type ReactNode } from "react";
import { Switch } from "./switch";

/**
 * ToggleRow — label/hint row with a Switch on the right, wrapped in a
 * hairline-bordered card. The lower-weight cousin of {@link SegmentedSetting}:
 * use this for everyday boolean preferences (enable feedback, override theme).
 *
 * Theme-aware via `--bg-primary`, `--border-default`, and the foreground
 * tokens — flips correctly in dark mode.
 */
export interface ToggleRowProps {
  label: string;
  hint?: ReactNode;
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Right-side slot to insert before the Switch (e.g. a small badge). */
  trailing?: ReactNode;
}

export const ToggleRow = forwardRef<HTMLDivElement, ToggleRowProps>(
  function ToggleRow(
    { label, hint, value, onChange, disabled, trailing },
    ref,
  ) {
    return (
      <Flex
        ref={ref}
        align="center"
        justify="space-between"
        gap={3}
        p={3}
        borderWidth="1px"
        borderColor="var(--border-default)"
        borderRadius="lg"
        bg="var(--bg-primary)"
        opacity={disabled ? 0.55 : 1}
      >
        <Box minW={0} flex={1}>
          <Text
            fontSize="sm"
            fontWeight={500}
            color="var(--text-primary)"
            m={0}
          >
            {label}
          </Text>
          {hint && (
            <Text
              fontSize="xs"
              color="var(--text-secondary-color)"
              mt={1}
              lineHeight="1.5"
            >
              {hint}
            </Text>
          )}
        </Box>
        <Flex align="center" gap={2} flexShrink={0}>
          {trailing}
          <Switch
            checked={value}
            onCheckedChange={(e: any) => onChange(e.checked)}
            disabled={disabled}
            colorPalette="blue"
          />
        </Flex>
      </Flex>
    );
  },
);

export default ToggleRow;
